import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import JsonNode from './JsonNode';
import { isObjectLike, getTopLevelEntrySizeBytes } from '../utils';

const JsonTree = ({
  data,
  searchTerm,
  onEdit,
  appearance,
  supportsTouch,
  onTogglePin,
  pinnedPathSet,
  onAddProperty,
  activeInlineAddPath,
  onInlineAddPathChange,
  priorityMap = {},
  exactMatchOnly = false,
  sortTopLevelBySize = false,
  showAllPropertiesOfMatchingNodes = true
}) => {
  const topLevelEntries = useMemo(() => {
    if (!isObjectLike(data) || Array.isArray(data)) return null;
    const sortedEntries = Object.entries(data).sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base', numeric: true }));

    if (sortTopLevelBySize) {
      return sortedEntries.slice().sort((a, b) => {
        const sizeA = getTopLevelEntrySizeBytes(a[0], a[1]) ?? -1;
        const sizeB = getTopLevelEntrySizeBytes(b[0], b[1]) ?? -1;
        if (sizeB !== sizeA) return sizeB - sizeA;
        return a[0].localeCompare(b[0], undefined, { sensitivity: 'base', numeric: true });
      });
    }

    const priorityList = priorityMap[''] || [];
    if (!priorityList.length) return sortedEntries;
    const prioritySet = new Set(priorityList);
    const prioritized = priorityList.map((key) => (Object.prototype.hasOwnProperty.call(data, key) ? [key, data[key]] : null)).filter(Boolean);
    return [...prioritized, ...sortedEntries.filter(([k]) => !prioritySet.has(k))];
  }, [data, priorityMap, sortTopLevelBySize]);

  const content = useMemo(() => {
    const entries = topLevelEntries;

    if (data === undefined) {
      return (
        <Typography variant="body2" sx={{ color: appearance === 'hud' ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}>
          Locking initial status...
        </Typography>
      );
    }

    if (!data && data !== 0 && data !== false) {
      return (
        <Typography variant="body2" sx={{ color: appearance === 'hud' ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}>
          No CLT status available.
        </Typography>
      );
    }

    if (!isObjectLike(data)) {
      return (
        <JsonNode
          label="map"
          value={data}
          path="map"
          keyPath={['map']}
          searchTerm={searchTerm}
          onEdit={onEdit}
          appearance={appearance}
          supportsTouch={supportsTouch}
          onTogglePin={onTogglePin}
          pinnedPathSet={pinnedPathSet}
          onAddProperty={onAddProperty}
          activeInlineAddPath={activeInlineAddPath}
          onInlineAddPathChange={onInlineAddPathChange}
          priorityMap={priorityMap}
          exactMatchOnly={exactMatchOnly}
          showAllPropertiesOfMatchingNodes={showAllPropertiesOfMatchingNodes}
        />
      );
    }

    if (Array.isArray(data)) {
      return data.length ? (
        data.map((item, idx) => (
          <JsonNode
            key={`map[${idx}]`}
            label={`[${idx}]`}
            value={item}
            path={`map[${idx}]`}
            keyPath={[idx]}
            defaultOpen={false}
            searchTerm={searchTerm}
            onEdit={onEdit}
            appearance={appearance}
            supportsTouch={supportsTouch}
            onTogglePin={onTogglePin}
            pinnedPathSet={pinnedPathSet}
            onAddProperty={onAddProperty}
            activeInlineAddPath={activeInlineAddPath}
            onInlineAddPathChange={onInlineAddPathChange}
            priorityMap={priorityMap}
            exactMatchOnly={exactMatchOnly}
            showAllPropertiesOfMatchingNodes={showAllPropertiesOfMatchingNodes}
          />
        ))
      ) : (
        <Typography variant="body2" sx={{ color: appearance === 'hud' ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}>
          (empty)
        </Typography>
      );
    }

    return entries?.length ? (
      <>
        {entries.map(([k, v]) => {
          const sizeBytes = appearance === 'hud' ? getTopLevelEntrySizeBytes(k, v) : null;
          const labelSuffix = typeof sizeBytes === 'number' ? ` (${sizeBytes.toLocaleString()} B)` : '';
          return (
            <JsonNode
              key={`map.${k}`}
              label={k}
              labelSuffix={labelSuffix}
              value={v}
              path={`map.${k}`}
              keyPath={[k]}
              defaultOpen={false}
              searchTerm={searchTerm}
              onEdit={onEdit}
              appearance={appearance}
              supportsTouch={supportsTouch}
              onTogglePin={onTogglePin}
              pinnedPathSet={pinnedPathSet}
              onAddProperty={onAddProperty}
              activeInlineAddPath={activeInlineAddPath}
              onInlineAddPathChange={onInlineAddPathChange}
              priorityMap={priorityMap}
              exactMatchOnly={exactMatchOnly}
              showAllPropertiesOfMatchingNodes={showAllPropertiesOfMatchingNodes}
            />
          );
        })}
      </>
    ) : (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: appearance === 'hud' ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}>
        (empty)
      </Typography>
    );
  }, [
    data,
    searchTerm,
    onEdit,
    appearance,
    supportsTouch,
    onTogglePin,
    pinnedPathSet,
    onAddProperty,
    activeInlineAddPath,
    onInlineAddPathChange,
    topLevelEntries,
    exactMatchOnly,
    showAllPropertiesOfMatchingNodes
  ]);

  return content;
};

JsonTree.propTypes = {
  data: PropTypes.any,
  searchTerm: PropTypes.string,
  onEdit: PropTypes.func,
  appearance: PropTypes.oneOf(['page', 'hud']),
  supportsTouch: PropTypes.bool,
  onTogglePin: PropTypes.func,
  pinnedPathSet: PropTypes.instanceOf(Set),
  onAddProperty: PropTypes.func,
  activeInlineAddPath: PropTypes.string,
  onInlineAddPathChange: PropTypes.func,
  priorityMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  exactMatchOnly: PropTypes.bool,
  sortTopLevelBySize: PropTypes.bool,
  showAllPropertiesOfMatchingNodes: PropTypes.bool
};

export default JsonTree;
